/*
 * VenueDetailPanel — rich hotel detail slide-over.
 *
 * When a user clicks "View details" on the small VenueDetailCard, this panel
 * slides in from the right (same pattern as CheckoutDrawer) showing a full
 * hotel booking experience: image gallery, description, amenities grid,
 * room type cards with per-type booking, and policies.
 */
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Star, Bed, Users, CaretLeft, CaretRight, MapPin, Clock, Shield,
  WifiHigh, Car, Barbell, SwimmingPool, ForkKnife, Wine, BellSimple,
  Flower, Briefcase, TShirt, Fan, Airplane, Dog, Lightning, UsersThree,
  TreePalm, Coffee, ArrowsClockwise, Buildings, Cube,
  Warning, Lock, CheckCircle, Heart, Prohibit,
} from '@phosphor-icons/react';

import logisticsApi from '../../lib/logisticsApi';

const AMENITY_ICONS = {
  wifi: WifiHigh, pool: SwimmingPool, gym: Barbell, spa: Flower,
  restaurant: ForkKnife, bar: Wine, parking: Car, room_service: BellSimple,
  concierge: Heart, business_center: Briefcase, laundry: TShirt,
  air_conditioning: Fan, airport_shuttle: Airplane, pet_friendly: Dog,
  ev_charging: Lightning, meeting_rooms: UsersThree, rooftop_terrace: TreePalm,
  minibar: Coffee, breakfast: Coffee, non_smoking: Prohibit,
  wheelchair: Prohibit, luggage_storage: Cube, '24h_front_desk': Clock,
  garden: TreePalm, tv: Buildings, desk: Briefcase, safe: Lock,
  bathrobe: Heart, lounge: Buildings, espresso_machine: Coffee,
  extra_bedding: Bed,
};

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'var(--ls-success)', icon: CheckCircle },
  closed: { label: 'Closed', color: 'var(--ls-danger)', icon: Lock },
  maintenance: { label: 'Maintenance', color: 'var(--ls-warning)', icon: Warning },
  fully_booked: { label: 'Fully Booked', color: 'var(--ls-danger)', icon: Lock },
};

const fmt = (minor) =>
  ((minor || 0) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });


/* ---------- Image Gallery ---------- */
const ImageGallery = ({ images = [] }) => {
  const [idx, setIdx] = useState(0);
  const scrollRef = useRef(null);

  const scrollTo = (i) => {
    setIdx(i);
    scrollRef.current?.children?.[i]?.scrollIntoView({
      behavior: 'smooth', block: 'nearest', inline: 'center',
    });
  };

  if (images.length === 0) return null;

  return (
    <div className="relative" data-testid="ls-detail-gallery">
      <div
        ref={scrollRef}
        className="flex gap-1 overflow-x-auto snap-x snap-mandatory scrollbar-hide"
        style={{ scrollSnapType: 'x mandatory' }}
        onScroll={(e) => {
          const el = e.target;
          const newIdx = Math.round(el.scrollLeft / el.clientWidth);
          if (newIdx !== idx && newIdx >= 0 && newIdx < images.length) setIdx(newIdx);
        }}
      >
        {images.map((src, i) => (
          <div
            key={i}
            className="shrink-0 w-full h-56 snap-center bg-cover bg-center"
            style={{
              backgroundImage: `url(${src})`,
              background: `url(${src}) center / cover, var(--ls-elevated)`,
            }}
          />
        ))}
      </div>
      {images.length > 1 && (
        <>
          <button
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.5)', color: '#fff' }}
            onClick={() => scrollTo(Math.max(0, idx - 1))}
            data-testid="ls-gallery-prev"
          >
            <CaretLeft size={16} weight="bold" />
          </button>
          <button
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.5)', color: '#fff' }}
            onClick={() => scrollTo(Math.min(images.length - 1, idx + 1))}
            data-testid="ls-gallery-next"
          >
            <CaretRight size={16} weight="bold" />
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => scrollTo(i)}
                className="w-2 h-2 rounded-full transition-all"
                style={{
                  background: i === idx ? 'var(--ls-primary)' : 'rgba(255,255,255,0.5)',
                  transform: i === idx ? 'scale(1.3)' : 'scale(1)',
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};


/* ---------- Amenities Grid ---------- */
const AmenitiesGrid = ({ amenities = [] }) => {
  if (amenities.length === 0) return null;
  return (
    <div data-testid="ls-detail-amenities">
      <p className="ls-overline mb-3" style={{ color: 'var(--ls-primary)' }}>Amenities</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {amenities.map((a) => {
          const Icon = AMENITY_ICONS[a] || CheckCircle;
          return (
            <div
              key={a}
              className="flex items-center gap-2 py-1.5 px-2.5 text-xs rounded"
              style={{ background: 'var(--ls-elevated)', color: 'var(--ls-secondary)' }}
            >
              <Icon size={14} weight="bold" style={{ color: 'var(--ls-primary)', flexShrink: 0 }} />
              <span className="capitalize">{a.replace(/_/g, ' ')}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};


/* ---------- Room Type Card ---------- */
const RoomTypeCard = ({ room, currencySymbol = 'Ÿ', onBook, disabled, disabledReason }) => (
  <div
    className="ls-surface p-4 flex flex-col gap-3"
    style={{ border: '1px solid var(--ls-border)' }}
    data-testid={`ls-room-type-${room.key}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <h4 className="ls-heading text-base font-bold">{room.label}</h4>
        <p className="text-xs mt-0.5" style={{ color: 'var(--ls-muted)' }}>{room.description}</p>
      </div>
      <div className="text-right shrink-0 ml-3">
        <p className="ls-mono text-xl font-bold" style={{ color: 'var(--ls-primary)' }}>
          {currencySymbol}{fmt(room.nightly_minor)}
        </p>
        <p className="text-[10px]" style={{ color: 'var(--ls-muted)' }}>/ night</p>
      </div>
    </div>
    <div className="flex flex-wrap gap-3 text-xs" style={{ color: 'var(--ls-muted)' }}>
      <span className="flex items-center gap-1"><Bed size={12} weight="bold" /> {room.bed}</span>
      <span className="flex items-center gap-1"><Users size={12} weight="bold" /> Up to {room.max_guests}</span>
      {room.available != null && (
        <span className="flex items-center gap-1">
          <CheckCircle size={12} weight="bold" style={{ color: 'var(--ls-success)' }} />
          {room.available} available
        </span>
      )}
    </div>
    {room.amenities?.length > 0 && (
      <div className="flex flex-wrap gap-1">
        {room.amenities.slice(0, 6).map((a) => {
          const Icon = AMENITY_ICONS[a] || CheckCircle;
          return (
            <span
              key={a}
              className="text-[10px] px-1.5 py-0.5 rounded flex items-center gap-0.5"
              style={{ background: 'var(--ls-elevated)', color: 'var(--ls-muted)' }}
            >
              <Icon size={9} weight="bold" /> {a.replace(/_/g, ' ')}
            </span>
          );
        })}
      </div>
    )}
    <button
      className="ls-btn-primary w-full mt-1"
      onClick={() => onBook?.(room)}
      disabled={disabled}
      title={disabledReason || `Book ${room.label}`}
      data-testid={`ls-book-room-${room.key}`}
    >
      Book {room.label}
    </button>
  </div>
);


/* ---------- Policies Section ---------- */
const PoliciesSection = ({ policies }) => {
  if (!policies) return null;
  return (
    <div data-testid="ls-detail-policies">
      <p className="ls-overline mb-3" style={{ color: 'var(--ls-primary)' }}>Policies</p>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="ls-elevated p-3 rounded">
          <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--ls-muted)' }}>Check-in</p>
          <p className="ls-mono text-sm font-semibold flex items-center gap-1.5">
            <Clock size={14} weight="bold" style={{ color: 'var(--ls-primary)' }} />
            {policies.check_in_time}
          </p>
        </div>
        <div className="ls-elevated p-3 rounded">
          <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--ls-muted)' }}>Check-out</p>
          <p className="ls-mono text-sm font-semibold flex items-center gap-1.5">
            <Clock size={14} weight="bold" style={{ color: 'var(--ls-primary)' }} />
            {policies.check_out_time}
          </p>
        </div>
      </div>
      {policies.house_rules?.length > 0 && (
        <div className="ls-elevated p-3 rounded">
          <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: 'var(--ls-muted)' }}>House Rules</p>
          <ul className="space-y-1.5">
            {policies.house_rules.map((rule, i) => (
              <li key={i} className="text-xs flex items-start gap-2" style={{ color: 'var(--ls-secondary)' }}>
                <Shield size={11} weight="fill" style={{ color: 'var(--ls-muted)', marginTop: 2, flexShrink: 0 }} />
                {rule}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};


/* ---------- Main Panel ---------- */
const VenueDetailPanel = ({
  open,
  onClose,
  venue,
  exerciseId,
  onBookRoomType,
  bookDisabledReason,
  currencySymbol = 'Ÿ',
}) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDetail = useCallback(async () => {
    if (!venue?.venue_id || !exerciseId) return;
    setLoading(true);
    setError(null);
    try {
      const d = await logisticsApi.venueDetail(exerciseId, venue.venue_id);
      setDetail(d);
    } catch (e) {
      setError(e.message || 'Failed to load venue details');
    } finally {
      setLoading(false);
    }
  }, [venue?.venue_id, exerciseId]);

  useEffect(() => {
    if (open && venue) fetchDetail();
    if (!open) { setDetail(null); setError(null); }
  }, [open, venue?.venue_id, fetchDetail]);

  const status = detail?.status || 'open';
  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.open;
  const StatusIcon = statusCfg.icon;

  return (
    <AnimatePresence>
      {open && venue && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-40"
            style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}
            data-testid="ls-detail-scrim"
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            className="ls-glass fixed top-0 right-0 h-full z-50 flex flex-col w-full sm:w-[520px]"
            style={{ borderLeft: '1px solid var(--ls-border)' }}
            data-testid="ls-detail-panel"
            role="dialog"
            aria-label="Venue details"
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-5 h-14 shrink-0"
              style={{ borderBottom: '1px solid var(--ls-border)' }}
            >
              <div className="flex items-center gap-2">
                <Bed size={16} weight="fill" style={{ color: 'var(--ls-primary)' }} />
                <span className="ls-heading text-sm font-bold">Venue Details</span>
              </div>
              <button
                onClick={onClose}
                className="ls-btn-ghost !p-2"
                aria-label="Close details"
                data-testid="ls-detail-close"
              >
                <X size={14} weight="bold" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <ArrowsClockwise size={24} className="animate-spin" style={{ color: 'var(--ls-primary)' }} />
                </div>
              ) : error ? (
                <div className="p-5 text-center">
                  <Warning size={32} weight="duotone" style={{ color: 'var(--ls-danger)' }} />
                  <p className="text-sm mt-3" style={{ color: 'var(--ls-danger)' }}>{error}</p>
                  <button className="ls-btn-ghost mt-3" onClick={fetchDetail}>Retry</button>
                </div>
              ) : detail ? (
                <>
                  {/* Gallery */}
                  <ImageGallery images={detail.images} />

                  <div className="px-5 py-5 space-y-6">
                    {/* Overview */}
                    <div>
                      <div className="flex items-start gap-2 mb-2">
                        <span className="ls-overline">{detail.subcategory || detail.category}</span>
                        {!!detail.stars && (
                          <span className="flex items-center gap-0.5">
                            {Array.from({ length: detail.stars }).map((_, i) => (
                              <Star key={i} size={10} weight="fill" style={{ color: 'var(--ls-primary)' }} />
                            ))}
                          </span>
                        )}
                        {detail.overridden && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ml-auto"
                            style={{ background: 'var(--ls-warning)', color: '#1a1408' }}>
                            Overridden
                          </span>
                        )}
                        {status !== 'open' && (
                          <span
                            className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ml-auto flex items-center gap-1"
                            style={{ background: statusCfg.color, color: '#fff' }}
                            data-testid="ls-detail-status"
                          >
                            <StatusIcon size={10} weight="fill" /> {statusCfg.label}
                          </span>
                        )}
                      </div>
                      <h3 className="ls-heading text-2xl font-bold leading-tight mb-1">{detail.name}</h3>
                      {detail.address && (
                        <p className="text-xs flex items-center gap-1 mb-3" style={{ color: 'var(--ls-muted)' }}>
                          <MapPin size={11} weight="fill" /> {detail.address}
                        </p>
                      )}
                      {detail.description && (
                        <p className="text-sm leading-relaxed" style={{ color: 'var(--ls-muted)' }}>
                          {detail.description}
                        </p>
                      )}
                    </div>

                    {/* Amenities */}
                    <AmenitiesGrid amenities={detail.amenities} />

                    {/* Room Types */}
                    {detail.room_types?.length > 0 && (
                      <div data-testid="ls-detail-rooms">
                        <p className="ls-overline mb-3" style={{ color: 'var(--ls-primary)' }}>Room Types</p>
                        <div className="space-y-3">
                          {detail.room_types.map((room) => (
                            <RoomTypeCard
                              key={room.key}
                              room={room}
                              currencySymbol={currencySymbol}
                              onBook={(r) => onBookRoomType?.(venue, r)}
                              disabled={!!bookDisabledReason || status !== 'open'}
                              disabledReason={
                                status !== 'open'
                                  ? `Venue is ${statusCfg.label.toLowerCase()}`
                                  : bookDisabledReason
                              }
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Policies */}
                    <PoliciesSection policies={detail.policies} />

                    {/* Price hint fallback */}
                    <div className="flex items-end justify-between pt-2" style={{ borderTop: '1px solid var(--ls-border)' }}>
                      <div>
                        <p className="ls-overline">Starting from</p>
                        <p className="ls-mono text-3xl font-bold" style={{ color: 'var(--ls-primary)' }}>
                          {currencySymbol}{fmt(detail.price_hint)}
                        </p>
                        <p className="text-[10px]" style={{ color: 'var(--ls-muted)' }}>/ night · simulated</p>
                      </div>
                      {detail.phone && (
                        <p className="text-xs" style={{ color: 'var(--ls-muted)' }}>{detail.phone}</p>
                      )}
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default VenueDetailPanel;
